import { useEffect, useState, type RefObject } from "react";

// How far below the screen something counts as near.
const NEAR = "1200px";

// True once the element is near the screen, or once the page has loaded and the browser is idle:
// media further down the page waits until the page's own files are in, so it doesn't hold up what
// is on screen, yet is in hand before anyone scrolls to it.
export const useNearOrIdle = (ref: RefObject<Element>): boolean => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (ready || !element) return;

    const near = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setReady(true);
      },
      { rootMargin: `${NEAR} 0px` },
    );
    near.observe(element);

    let idle: number | undefined;
    const whenIdle = () => {
      // Safari has no requestIdleCallback
      idle =
        typeof window.requestIdleCallback === "function"
          ? window.requestIdleCallback(() => setReady(true), { timeout: 3000 })
          : window.setTimeout(() => setReady(true), 500);
    };
    if (document.readyState === "complete") whenIdle();
    else window.addEventListener("load", whenIdle, { once: true });

    return () => {
      near.disconnect();
      window.removeEventListener("load", whenIdle);
      if (idle === undefined) return;
      if (typeof window.cancelIdleCallback === "function") window.cancelIdleCallback(idle);
      else window.clearTimeout(idle);
    };
  }, [ref, ready]);

  return ready;
};
